import { useEffect, useRef, useState } from "react"
import { Link, NavLink, useNavigate } from "react-router-dom"
import { cn } from "../lib/cn"
import { useLogout, useMe } from "../lib/hooks"
import { useToast } from "./ui/toast"

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          "text-sm font-medium transition-colors",
          isActive ? "text-accent" : "text-text-secondary hover:text-text-primary",
        )
      }
    >
      {label}
    </NavLink>
  )
}

function UserMenu() {
  const me = useMe()
  const logout = useLogout()
  const nav = useNavigate()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // menuitem の DOM ref (roving focus 用)。順序 = [Settings, ログアウト]。
  const menuItemsRef = useRef<(HTMLButtonElement | null)[]>([])

  // 開閉に伴うフォーカス制御 (role="menu" の WAI-ARIA Authoring Practices に準拠)
  useEffect(() => {
    if (open) {
      // 開いた直後に最初の menuitem へフォーカス
      menuItemsRef.current[0]?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return
      const items = menuItemsRef.current.filter((el): el is HTMLButtonElement => el !== null)
      if (items.length === 0) return
      const active = document.activeElement as HTMLElement | null
      // biome useIndexOf: 単一値の等価比較なので findIndex → indexOf に簡略化
      const currentIndex = active instanceof HTMLButtonElement ? items.indexOf(active) : -1
      const delta = e.key === "ArrowDown" ? 1 : -1
      // 未フォーカス時は ArrowDown → 0、ArrowUp → 末尾
      const nextIndex =
        currentIndex === -1
          ? e.key === "ArrowDown"
            ? 0
            : items.length - 1
          : (currentIndex + delta + items.length) % items.length
      items[nextIndex]?.focus()
      e.preventDefault()
    }
    document.addEventListener("mousedown", handleDown)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleDown)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  const user = me.data?.user ?? null
  if (!user) return null

  // 表示名がなければ email から先頭を採用 (Supabase user_metadata.full_name 未設定時)
  const displayName = user.display_name || user.email.split("@")[0]
  const initial = (displayName || user.email).charAt(0).toUpperCase()
  const roleLabel = user.is_admin ? "Admin" : "Member"

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        id="user-menu-trigger"
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex size-8 items-center justify-center rounded-full bg-accent text-sm font-semibold text-text-on-accent transition-opacity hover:opacity-90"
        aria-label="アカウントメニューを開く"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="user-menu"
      >
        {initial}
      </button>

      {open ? (
        <div
          id="user-menu"
          role="menu"
          aria-labelledby="user-menu-trigger"
          aria-orientation="vertical"
          className="absolute right-0 top-10 z-40 w-60 overflow-hidden rounded-lg border border-border-default bg-surface-2 shadow-lg"
        >
          <div className="flex flex-col gap-1 p-3">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-text-primary">{displayName}</span>
              <span className="rounded-full bg-border-default px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                {roleLabel}
              </span>
            </div>
            <span className="truncate text-xs text-text-muted">{user.email}</span>
          </div>
          <div className="h-px bg-border-subtle" />
          <button
            ref={(el) => {
              menuItemsRef.current[0] = el
            }}
            type="button"
            onClick={() => {
              setOpen(false)
              nav("/settings")
            }}
            role="menuitem"
            tabIndex={-1}
            className="block w-full px-3 py-2.5 text-left text-[13px] text-text-primary hover:bg-surface focus-visible:bg-surface focus-visible:outline-none"
          >
            Settings
          </button>
          <div className="h-px bg-border-subtle" />
          <button
            ref={(el) => {
              menuItemsRef.current[1] = el
            }}
            type="button"
            disabled={logout.isPending}
            onClick={() => {
              setOpen(false)
              logout.mutate(undefined, {
                onError: (err) =>
                  toast.show({
                    title: "ログアウトに失敗しました",
                    description: err instanceof Error ? err.message : undefined,
                    variant: "error",
                  }),
              })
            }}
            role="menuitem"
            tabIndex={-1}
            className="block w-full px-3 py-2.5 text-left text-[13px] text-[#ff8080] hover:bg-surface focus-visible:bg-surface focus-visible:outline-none disabled:cursor-wait disabled:opacity-60"
          >
            ログアウト
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function AppBar() {
  return (
    <header className="sticky top-0 z-30 border-b border-border-subtle bg-surface/90 backdrop-blur">
      <div className="flex items-center justify-between px-8 py-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 transition-opacity hover:opacity-80"
          aria-label="トップへ"
        >
          <span className="size-2 rounded-full bg-accent" />
          <span className="text-[15px] font-semibold text-text-primary">OMC</span>
        </Link>
        <nav className="flex items-center gap-7">
          <NavItem to="/submit" label="New" />
          <NavItem to="/runs" label="Runs" />
          <NavItem to="/settings" label="Settings" />
          <UserMenu />
        </nav>
      </div>
    </header>
  )
}
